import {
  type QueryClient,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { StatusCode, type CustomerPairingRatingDetail } from '@hungryroot/rest';
import {
  getApiV2CustomersSelfPairingRatingsPairingId,
  type Params,
} from '@hungryroot/rest/api/v2/customers/self/pairing_ratings/[pairing_id]/get';

type UseSelfPairingRatingProps = {
  params: Params;
  options?: Omit<
    UseQueryOptions<CustomerPairingRatingDetail>,
    'queryFn' | 'queryKey'
  >;
};

export function getQueryKey({ pairing_id }: Params) {
  return ['Customer', 'Pairing Rating', { pairing_id }];
}

export function invalidateQuery({
  pairing_id,
  queryClient,
}: Pick<Params, 'pairing_id'> & { queryClient: QueryClient }) {
  queryClient.invalidateQueries({
    queryKey: getQueryKey({ pairing_id }),
  });
}

export function useSelfPairingRating(props: UseSelfPairingRatingProps) {
  const { params, options } = props;

  return useQuery({
    queryKey: getQueryKey(params),
    queryFn: async () => {
      const res = await getApiV2CustomersSelfPairingRatingsPairingId(params);

      if (res.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }

      return res.data;
    },
    ...options,
  });
}
